import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { FaGift, FaHeart, FaPaypal, FaApple, FaMobile } from 'react-icons/fa';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
  transition: color 0.3s ease;
`;

const TipCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: ${props => props.theme.shadow};
  text-align: center;
  margin-bottom: 2rem;
  border: 1px solid ${props => props.theme.border};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadowHover};
  }
`;

const TipIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: white;
  font-size: 2rem;
`;

const TipTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const TipDescription = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.6;
  margin-bottom: 2rem;
  transition: color 0.3s ease;
`;

const PaymentSection = styled.div`
  background: ${props => props.theme.secondaryBg};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  border: 1px solid ${props => props.theme.border};
  transition: all 0.3s ease;
  margin-top: 2rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadowHover};
  }
`;

const PaymentTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const PaymentDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 2rem;
  line-height: 1.6;
  transition: color 0.3s ease;
`;

const PaymentMethods = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PaymentMethodIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.bgColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const TipButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #0070ba, #005ea6);
  color: white;
  padding: 1.25rem 2.5rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  margin-top: 1rem;
  box-shadow: 0 4px 12px rgba(0, 112, 186, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 112, 186, 0.4);
    color: white;
    text-decoration: none;
    background: linear-gradient(135deg, #005ea6, #004a8c);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const StatsSection = styled.div`
  background: ${props => props.theme.secondaryBg};
  border-radius: 16px;
  padding: 2rem;
  margin-top: 3rem;
  border: 1px solid ${props => props.theme.border};
  transition: background-color 0.3s ease;
`;

const StatsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  text-align: center;
  margin-bottom: 1.5rem;
  transition: color 0.3s ease;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: ${props => props.theme.cardBg};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  transition: all 0.3s ease;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.primary};
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryText};
  font-weight: 500;
  transition: color 0.3s ease;
`;

const MessageSection = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 16px;
  padding: 2rem;
  margin-top: 2rem;
  border: 1px solid ${props => props.theme.border};
  text-align: center;
  transition: background-color 0.3s ease;
`;

const MessageTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const MessageText = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.6;
  transition: color 0.3s ease;
`;

const TipJarPage = () => {
  const { theme } = useTheme();

  return (
    <Container theme={theme}>

      <TipCard theme={theme}>
        <TipIcon>
          <FaGift />
        </TipIcon>
        <TipTitle theme={theme}>Support Our Work</TipTitle>
        <TipDescription theme={theme}>
          Your tips help us maintain and improve our prediction models, add new features, and keep the platform running smoothly. Every dollar counts!
        </TipDescription>
        
        <PaymentSection theme={theme}>
          <PaymentTitle theme={theme}>Secure Payment Options</PaymentTitle>
          <PaymentDescription theme={theme}>
            Choose any amount you want using your preferred payment method (PayPal, Venmo, Apple Pay).
          </PaymentDescription>
          
          <PaymentMethods>
            <PaymentMethodIcon bgColor="#0070ba">
              <FaPaypal />
            </PaymentMethodIcon>
            <PaymentMethodIcon bgColor="#008CFF">
              <FaMobile />
            </PaymentMethodIcon>
            <PaymentMethodIcon bgColor="#000000">
              <FaApple />
            </PaymentMethodIcon>
          </PaymentMethods>
          
          <TipButton 
            href="https://www.paypal.com/ncp/payment/R9WYJZZRB8ZBW" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Send Tip
          </TipButton>
        </PaymentSection>
      </TipCard>

      <StatsSection theme={theme}>
        <StatsTitle theme={theme}>Your Support Makes a Difference</StatsTitle>
        <StatsGrid>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>100%</StatNumber>
            <StatLabel theme={theme}>Free Platform</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>24/7</StatNumber>
            <StatLabel theme={theme}>Model Updates</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>∞</StatNumber>
            <StatLabel theme={theme}>Improvements</StatLabel>
          </StatCard>
        </StatsGrid>
      </StatsSection>

      <MessageSection theme={theme}>
        <MessageTitle theme={theme}>
          <FaHeart style={{ color: '#ff6b6b', marginRight: '0.5rem' }} />
          Thank You!
        </MessageTitle>
        <MessageText theme={theme}>
          Whether you tip or not, we appreciate you using SlickPicks! Our goal is to help you make better predictions, and your success is our success. 
          If you've had a good run with our predictions, consider sharing the love. Every tip helps us keep the platform free and continuously improving.
        </MessageText>
      </MessageSection>
    </Container>
  );
};

export default TipJarPage;
